import React, { useState } from 'react';
import { Box, Grid, makeStyles, CircularProgress, Theme } from '@material-ui/core';
import { Redirect } from 'react-router';
import clsx from 'clsx';
import _ from 'lodash';
import { EntrepreneurExecutiveSummary,
  EntrepreneurIntro,
  EntrepreneurCompanyDetails,
  EntrepreneurContact,
  EntrepreneurProfileBody,
  Role,
  EntrepreneurFinanceUpdateBody } from '../../types/types';
import EntrepreneurFinanceEdit from './EntrepreneurFinanceEdit';
import EntExecSummaryEdit, { unpublishedExecValidationSchema } from './EntExecSummaryEdit';
import EntrepreneurFinancePanel from './EntrepreneurFinancePanel';
import EntrepreneurContactPanel from './EntrepreneurContactPanel';
import EntCompanyDetailsPanel from './EntCompanyDetailsPanel';
import EntrepreneurIntroPanel from './EntrepreneurIntroPanel';
import EntrepreneurPitchPanel, { PitchPanelItem } from './EntrepreneurPitchPanel';
import EntExecSummaryPanel from './EntExecSummaryPanel';
import EntrepreneurContactEdit, { unpublishedContactValidationSchema } from './EntrepreneurContactEdit';
import EntCompanyDetailsEdit, { unpublishedDetailsValidationSchema } from './EntCompanyDetailsEdit';
import EntrepreneurIntroEdit from './EntrepreneurIntroEdit';
import { useEntContext, useUser } from '../../contexts';
import PublishingGuide from '../../components/PublishingGuide';
import { EntProfileElements } from '../../components/PublishingGuide/publishingLogic';
import NoDealsGuide from '../../components/PublishingGuide/NoDealsGuide';
import { entPublishedIntroSchema, entUnpublishedIntroSchema, entPublishedContactInformation, entPublishedCompanyDetails, entPublishedExecSummary } from '../../lib/validationSchemas';

const useStyles = makeStyles((theme: Theme) => ({
  entreRoot: {
    background: theme.palette.background.default
  },
  panelBackground: {
    background: '#FFFFFF'
  },
  panelBorder: {
    border: '1px solid #DAD9DF',
    borderRadius: 8
  }
}));

interface EntProfileProps {
  profile?: EntrepreneurProfileBody
  loading: boolean
}

const EntrepreneurProfile: React.FC<EntProfileProps> = ({
  profile,
  loading
}) => {
  const styles = useStyles();
  const [editFinance, setEditFinance] = useState<boolean>(false);
  const [editIntro, setEditIntro] = useState<PitchPanelItem>(PitchPanelItem.None);
  const [editExecSummary, setEditExecSummary] = useState<boolean>(false);
  const [editContact, setEditContact] = useState<boolean>(false);
  const [editCompanyDetails, setEditCompanyDetails] = useState<boolean>(false);

  const { updateProfile, isEditable, setIsSelectingCompanyLogo } = useEntContext();
  const { rootRedirect, setUserIsSelectingAvatar, userInfo } = useUser();

  const handleEditIntro = () => {
    if (editIntro) {
      setEditIntro(PitchPanelItem.None);
    } else {
      setEditIntro(PitchPanelItem.General);
    }
  };

  const handleEditFinance = () => {
    setEditFinance(!editFinance);
  };

  const handleEditMainPanel = () => {
    setEditExecSummary(!editExecSummary);
  };

  const handleEditContact = () => {
    setEditContact(!editContact);
  };

  const handleEditSidePanel = () => {
    setEditCompanyDetails(!editCompanyDetails);
  };

  const handleUpdateIntro = async (values: EntrepreneurIntro) => {
    let castValues = null;
    if (!profile?.isPublished) {
      castValues = entUnpublishedIntroSchema.cast(values) as EntrepreneurIntro;
    } else {
      castValues = entPublishedIntroSchema.cast(values) as EntrepreneurIntro;
    }
    const success = await updateProfile({ intro: castValues });
    if (success) {
      setEditIntro(PitchPanelItem.None);
    }
  };

  const handleFinanceUpdate = async (values: EntrepreneurFinanceUpdateBody) => {
    const success = await updateProfile({ finance: values });
    if (success) {
      setEditFinance(false);
    }
  };

  const handleMainPanelUpdate = async (values: EntrepreneurExecutiveSummary) => {
    let castValues = null;
    if (!profile?.isPublished) {
      castValues = unpublishedExecValidationSchema.cast(values) as EntrepreneurExecutiveSummary;
    } else {
      castValues = entPublishedExecSummary.cast(values) as EntrepreneurExecutiveSummary;
    }
    const success = await updateProfile({ executiveSummary: castValues });
    if (success) {
      setEditExecSummary(false);
    }
  };

  const handleContactUpdate = async (values: EntrepreneurContact) => {
    let castValues = null;
    if (!profile?.isPublished) {
      castValues = unpublishedContactValidationSchema.cast(values) as EntrepreneurContact;
    } else {
      castValues = entPublishedContactInformation.cast(values) as EntrepreneurContact;
    }
    const success = await updateProfile({ contact: castValues });
    if (success) {
      setEditContact(false);
    }
  };

  const handleSidePanelUpdate = async (values: EntrepreneurCompanyDetails) => {
    let castValues = null;
    if (!profile?.isPublished) {
      castValues = unpublishedDetailsValidationSchema.cast(values) as EntrepreneurCompanyDetails;
    } else {
      castValues = entPublishedCompanyDetails.cast(values) as EntrepreneurCompanyDetails;
    }
    const success = await updateProfile({ companyDetails: castValues });
    if (success) {
      setEditCompanyDetails(false);
    }
  };

  const handlePublishingGuideClick = (clicked: string) => {
    switch (clicked) {
      case EntProfileElements.GeneralInfo:
        setEditIntro(PitchPanelItem.General);
        break;
      case EntProfileElements.ContactInformation:
        setEditContact(true);
        break;
      case EntProfileElements.ExecutiveSummary:
        setEditExecSummary(true);
        break;
      case EntProfileElements.FinancialSummary:
        setEditFinance(true);
        break;
      case EntProfileElements.CompanyDetails:
        setEditCompanyDetails(true);
        break;
      case EntProfileElements.CompanyLogo:
        if (setIsSelectingCompanyLogo) {
          setIsSelectingCompanyLogo(true);
        }
        break;
      case EntProfileElements.ContactPhoto:
        setUserIsSelectingAvatar(true);
        break;
      default:
        break;
    }
  };

  if (loading || profile === undefined) return <CircularProgress />;

  const editable: boolean = isEditable;

  if (rootRedirect) {
    return (<Redirect to={rootRedirect} />);
  }

  const handlePitchPanelEditClick = (item: PitchPanelItem) => {
    setEditIntro(item);
  };


  const Guide = () => {
    const PG = () => (
      <>
        <PublishingGuide profile={profile} role={Role.Ent} handleClick={handlePublishingGuideClick} />
        <br />
      </>
    );

    if (profile && !profile.isPublished && isEditable) {
      return <PG />;
    }
    return null;
  };

  return (
    <>
      <Guide />
      <Grid container className={styles.entreRoot} wrap='nowrap' spacing={1} direction='row' justify='space-between'>
        <EntrepreneurIntroEdit
          introPanelBody={profile}
          profileId={profile.id}
          focusedItem={editIntro}
          isPublished={profile.isPublished}
          handleClose={handleEditIntro}
          handleSubmit={handleUpdateIntro}
        />
        <EntrepreneurFinanceEdit
          open={editFinance}
          profile={{
            financeRaised: profile.financeRaised,
            financeSoughtHigh: profile.financeSoughtHigh,
            financeSoughtLow: profile.financeSoughtLow,
            financialProjection: profile.financialProjection,
            currentRevenue: profile.currentRevenue
          }}
          handleSubmit={handleFinanceUpdate}
          handleClose={handleEditFinance}
          isPublished={profile.isPublished}
        />
        <EntExecSummaryEdit
          open={editExecSummary}
          mainPanelBody={{
            management: profile.management,
            market: profile.market,
            background: profile.background,
            distribution: profile.distribution,
            financialProjection: profile.financialProjection,
            competition: profile.competition
          }}
          isPublished={profile.isPublished}
          handleSubmit={handleMainPanelUpdate}
          handleClose={handleEditMainPanel}
        />
        <EntrepreneurContactEdit
          contactBody={{
            contactName: profile.contactName,
            contactTitle: profile.contactTitle,
            street1: profile.street1,
            street2: profile.street2,
            city: profile.city,
            state: profile.state,
            country: profile.country,
            zipCode: profile.zipCode,
            email: profile.email,
            phone: profile.phone,
            website: profile.website
          }}
          open={editContact}
          isPublished={profile.isPublished}
          handleClose={handleEditContact}
          handleSubmit={handleContactUpdate}
        />
        <EntCompanyDetailsEdit
          sidePanelBody={{
            managementTeam: profile.managementTeam,
            employeeCount: profile.employeeCount,
            bank: profile.bank,
            auditor: profile.auditor,
            lawFirm: profile.lawFirm,
            capTableManagement: profile.capTableManagement,
            hrProvider: profile.hrProvider,
            currentInvestor: profile.currentInvestor,
            fundUsage: profile.fundUsage,
            founderExit: profile.founderExit,
            founderFemale: profile.founderFemale,
            founderUnderrepresented: profile.founderUnderrepresented
          }}
          open={editCompanyDetails}
          isPublished={profile.isPublished}
          handleClose={handleEditSidePanel}
          handleSubmit={handleSidePanelUpdate}
        />
        <Grid item id='entrepreneur-main-panel' xs={8}>
          <Box className={clsx(styles.panelBackground, styles.panelBorder)}>
            <EntrepreneurIntroPanel
              introBody={profile}
              editable={editable}
              openEdit={handleEditIntro}
            />
            <EntrepreneurPitchPanel
              profile={profile}
              handleEditItem={(editable && handlePitchPanelEditClick) || undefined}
            />
            <EntExecSummaryPanel
              profile={profile}
              editable={editable}
              openEdit={handleEditMainPanel}
            />
          </Box>
        </Grid>
        <Grid item id='entrepreneur-side-panel' xs={4}>
          <EntrepreneurFinancePanel
            profile={profile}
            editable={editable}
            openEdit={handleEditFinance}
          />
          <EntrepreneurContactPanel
            profile={profile}
            editable={editable}
            openEdit={handleEditContact}
          />
          <EntCompanyDetailsPanel
            profile={profile}
            editable={editable}
            openEdit={handleEditSidePanel}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default EntrepreneurProfile;
